import React from 'react';
import { Select, DatePicker, TimePicker, Button, Tag, Space, Typography } from 'antd';
import { SearchOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFilters } from '../../context/FilterContext';
import { useAuth } from '../../context/AuthContext';

const granOpts = [
  { value: '15min', label: '15 Minutes (Aggregated)' },
  { value: '15min-raw', label: '15 Minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

export default function GlobalFilterBar({ onApply, extra, style, isRevenue, isMarket }) {
  const { plants } = useAuth();
  const f = useFilters();
  const solar = (plants || []).filter(p => p.plant_type === 'solar');
  const wind = (plants || []).filter(p => p.plant_type === 'wind');

  const maxAllowedDate = isMarket ? dayjs().add(1, 'day') : dayjs();

  const handleApply = () => { if (onApply) onApply(); };

  const handleStartDateChange = (val) => {
    f.setDateFrom(val);
    if (val && f.dateTo && val.isAfter(f.dateTo, 'day')) {
      f.setDateTo(val);
    }
  };

  const handleQuickSelect = (type) => {
    const today = dayjs();
    if (type === 'ytd') {
      f.setDateFrom(today.startOf('year'));
      f.setDateTo(today.subtract(1, 'day'));
      f.setGranularity('monthly');
    } else if (type === 'mtd') {
      f.setDateFrom(today.startOf('month'));
      f.setDateTo(today.subtract(1, 'day'));
      f.setGranularity('daily');
    } else if (type === 'weekly') {
      const yesterday = today.subtract(1, 'day');
      f.setDateFrom(yesterday.subtract(7, 'day'));
      f.setDateTo(yesterday);
      f.setGranularity('daily');
    } else if (type === 'day-before') {
      f.setDateFrom(today.subtract(1, 'day'));
      f.setDateTo(today.subtract(1, 'day'));
      f.setGranularity('hourly');
    } else if (type === 'today') {
      f.setDateFrom(today);
      f.setDateTo(today);
      f.setGranularity('hourly');
    } else if (type === 'tomorrow') {
      const tomorrow = today.add(1, 'day');
      f.setDateFrom(tomorrow);
      f.setDateTo(tomorrow);
      f.setGranularity('hourly');
    }
  };

  return (
    <div style={{
      width: 220,
      minWidth: 220,
      background: '#fff',
      borderLeft: '1px solid #f0f0f0',
      padding: '16px 12px',
      height: '100vh',
      position: 'sticky',
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      overflowY: 'auto',
      zIndex: 100,
      ...style
    }}>


      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 4, color: '#4b5563', fontSize: 13 }}>Granularity</Typography.Text>
        <Select value={f.granularity} onChange={f.setGranularity} style={{ width: '100%' }} options={granOpts} />
      </div>

      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 4, color: '#4b5563', fontSize: 13 }}>Start Date</Typography.Text>
        <DatePicker
          value={f.dateFrom}
          onChange={handleStartDateChange}
          format="DD-MMM-YY"
          disabledDate={(current) => current && current.isAfter(maxAllowedDate, 'day')}
          style={{ width: '100%' }}
          placeholder="Start"
        />
      </div>

      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 4, color: '#4b5563', fontSize: 13 }}>End Date</Typography.Text>
        <DatePicker
          value={f.dateTo}
          onChange={f.setDateTo}
          format="DD-MMM-YY"
          disabledDate={(current) => {
            return (current && current.isAfter(maxAllowedDate, 'day')) || (current && f.dateFrom && current.isBefore(f.dateFrom, 'day'));
          }}
          style={{ width: '100%' }}
          placeholder="End"
        />
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
        <Button size="small" onClick={() => handleQuickSelect('ytd')} style={{ fontSize: 11, flex: '1 0 30%' }}>YTD</Button>
        <Button size="small" onClick={() => handleQuickSelect('mtd')} style={{ fontSize: 11, flex: '1 0 30%' }}>MTD</Button>
        <Button size="small" onClick={() => handleQuickSelect('weekly')} style={{ fontSize: 11, flex: '1 0 30%' }}>Weekly</Button>
        {!isRevenue && !isMarket && (
          <>
            <Button size="small" onClick={() => handleQuickSelect('day-before')} style={{ fontSize: 11, flex: '1 0 45%' }}>Day Before</Button>
            <Button size="small" onClick={() => handleQuickSelect('today')} style={{ fontSize: 11, flex: '1 0 45%' }}>Today</Button>
          </>
        )}
        {isMarket && (
          <>
            <Button size="small" onClick={() => handleQuickSelect('day-before')} style={{ fontSize: 11, flex: '1 0 28%' }}>Day Before</Button>
            <Button size="small" onClick={() => handleQuickSelect('today')} style={{ fontSize: 11, flex: '1 0 28%' }}>Today</Button>
            <Button size="small" onClick={() => handleQuickSelect('tomorrow')} style={{ fontSize: 11, flex: '1 0 28%' }}>+1 Day</Button>
          </>
        )}
      </div>

      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 4, color: '#4b5563', fontSize: 13 }}>Time Range</Typography.Text>
        <div style={{ display: 'flex', gap: 4 }}>
          <TimePicker value={f.startTime} onChange={f.setStartTime} format="HH:mm" style={{ flex: 1 }} placeholder="Start" minuteStep={15} />
          <TimePicker value={f.endTime} onChange={f.setEndTime} format="HH:mm" style={{ flex: 1 }} placeholder="End" minuteStep={15} />
        </div>
      </div>



      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleApply} block size="middle">Apply</Button>
        <Button icon={<UndoOutlined />} onClick={() => { f.resetFilters(); }} block size="middle">Reset</Button>
        {extra && <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4 }}>{extra}</div>}
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20, marginTop: 'auto' }}>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>ACTIVE FILTERS</Typography.Text>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>

          <Tag style={{ margin: 0 }}>{f.dateFrom?.format('DD-MMM-YYYY') || 'All'} to {f.dateTo?.format('DD-MMM-YYYY') || 'All'}</Tag>
          {f.startTime && <Tag color="orange" style={{ margin: 0 }}>{f.startTime.format('HH:mm')}</Tag>}
          {f.endTime && <Tag color="orange" style={{ margin: 0 }}>{f.endTime.format('HH:mm')}</Tag>}
          <Tag color="purple" style={{ margin: 0 }}>{granOpts.find(o => o.value === f.granularity)?.label}</Tag>
        </div>
      </div>
    </div>
  );
}
